import { Injectable, Signal, signal } from '@angular/core';

type ToastType = 'error' | 'info' | 'success' | 'warning';

export interface Toast {
  duration: number;
  message: string;
  status: 'show' | 'hide';
  title: string;
  type: ToastType;
}

const defaultDuration = 5_000;

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private autoHideTimeout: ReturnType<typeof setTimeout> | null= null;
  private queue = signal<Toast[]>([]);
  private currentToast = signal<Toast | null>(null);
  private isProcessing = false;

  getCurrentToast(): Signal<Toast | null> {
    return this.currentToast.asReadonly();
  }

  hideCurrentToast(): void {
    if (!this.currentToast() || this.currentToast()?.status === 'hide') {
      return;
    }

    if (this.autoHideTimeout) {
      clearTimeout(this.autoHideTimeout);
      this.autoHideTimeout = null;
    }

    this.hideAndCleanup();
  }

  showInfo(title: string, message: string, duration = defaultDuration): void {
    this.addToast('info', title, message, duration);
  }

  showSuccess(title: string, message: string, duration = defaultDuration): void {
    this.addToast('success', title, message, duration);
  }

  showWarning(title: string, message: string, duration = defaultDuration): void {
    this.addToast('warning', title, message, duration);
  }

  showError(title: string, message: string, duration = defaultDuration): void {
    this.addToast('error', title, message, duration);
  }

  private addToast(type: ToastType, title: string, message: string, duration: number): void {
    const toast: Toast = {
      duration,
      message,
      status: 'show',
      title,
      type,
    };
    this.queue.update((queue) => [...queue, toast]);
    this.processQueue();
  }

  private hideAndCleanup(): void {
    this.currentToast.update((toast) => ({
      ...toast!,
      status: 'hide',
    }));
    setTimeout(() => {
      this.currentToast.set(null);
      this.autoHideTimeout = null;
      this.isProcessing = false;
      this.processQueue();
    }, 300);
  }

  private processQueue(): void {
    if (this.isProcessing || this.queue().length === 0) {
      return;
    }

    this.isProcessing = true;
    const nextToast = this.queue()[0];
    this.currentToast.set(nextToast);
    this.queue.update((queue) => queue.slice(1));
    this.autoHideTimeout = setTimeout(() => this.hideAndCleanup(), nextToast.duration);
  }
}
